import { getKey } from "pax-ui/core";
import {
  AnsweredUnderwritingQuestionDto,
  OrganizationBeneficiaryDto,
  PersonBeneficiaryDto,
} from "src/app/quote/term-life-insurance/services/backend/dto/shared.dto";
import { TermLifeInsuranceStaticDocumentType } from "src/app/quote/term-life-insurance/services/backend/shared/term-life-insurance-document-type";
import { InvalidOperationException } from "src/app/shared/exceptions/exceptions";
import { DateTimeService } from "src/app/shared/services/date-time.service";
import { ObjectService } from "src/app/shared/services/object.service";
import { UnreachableCaseError } from "ts-essentials";

import {
  ConversionCapitalDevelopmentDto,
  ProductConfigurationDto,
  TermDocumentDto,
  UnderwritingQuestionDto,
} from "src/app/quote/term-life-insurance/services/backend/response/models/shared-quote-response.dto";
import { TermLifeInsuranceQuoteResponseDto } from "src/app/quote/term-life-insurance/services/backend/response/term-life-insurance-quote-response.dto";
import { DEFAULT_RESET_VALUE } from "src/app/shared/store/shared/http-request-state";
import {
  BeneficiarySubType,
  ConversionCapitalDevelopment,
  OrganizationBeneficiary,
  PersonBeneficiary,
  ProductConfiguration,
  QuoteFeatureState,
  StaticDocumentType,
  TermDocument,
  UnderwritingQuestion,
  UnderwritingQuestionAnswer,
} from "../../quote.state";

export class QuoteReducerMapperService {
  static mapQuoteSuccessResponseToState(
    existingQuoteFeatureState: QuoteFeatureState,
    // Product Agnostic
    dto: TermLifeInsuranceQuoteResponseDto,
    // Product Specific
    productConfigurationDto: ProductConfigurationDto | undefined,
  ) {
    const quoteFeatureState: QuoteFeatureState = {
      /////////////////////////////////////////////////////
      // General
      /////////////////////////////////////////////////////

      isBusy: false,
      error: undefined,
      quoteId: dto.quoteId,
      product: dto.product.product,
      applicationId: dto.applicationId,
      attributionCode: dto.attributionCode,

      /////////////////////////////////////////////////////
      // Applicant
      /////////////////////////////////////////////////////

      mobilePhoneNumber: dto.applicant?.mobilePhoneNumber,
      email: dto.applicant?.emailAddress,
      monthOfPregnancy: dto.applicant?.monthOfPregnancy,
      gender: dto.applicant?.gender,
      firstName: dto.applicant?.firstName,
      lastName: dto.applicant?.lastName,
      nationalityCode: dto.applicant?.nationalityCode,
      swissResidencePermit: dto.applicant?.swissResidencePermit,
      dateOfBirth: DateTimeService.tryParseIsoDate(dto.applicant?.dateOfBirth),
      technicalAgeInYears: dto.applicant?.technicalAgeInYears,
      addressLines: dto.applicant?.addressLines,
      city: dto.applicant?.city,
      postcode: dto.applicant?.postcode,
      country: dto.applicant?.country,
      civilStatus: dto.applicant?.civilStatus,
      isSmoker: dto.applicant?.isSmoker,
      occupationId: dto.applicant?.occupationId,
      hasTermsAccepted: dto.applicant?.hasTermsAccepted,
      heightInCm: dto.applicant?.heightInCm,
      weightInKg: dto.applicant?.weightInKg,
      isLegacyApplication: dto.applicant?.isLegacyApplication,
      hasUsCitizenship: dto.applicant?.hasSwissTaxResidence,
      hasSwissTaxResidence: dto.applicant?.hasSwissTaxResidence,

      /////////////////////////////////////////////////////
      // Tariff
      /////////////////////////////////////////////////////

      insuranceStartDate: DateTimeService.tryParseIsoDate(
        dto.product.insuranceStartDate,
      ),
      insuredSum: dto.product.insuredSum,
      insuranceTermInYears: dto.product.insuranceTermInYears,

      /////////////////////////////////////////////////////
      // Beneficiaries
      /////////////////////////////////////////////////////

      useStandardBeneficiaries: dto.useStandardBeneficiaries,
      beneficiaries: QuoteReducerMapperService.tryMapBeneficiariesToState(
        dto.personBeneficiaries,
        dto.organizationBeneficiaries,
      ),

      /////////////////////////////////////////////////////
      // Calculated values from Quicksale
      /////////////////////////////////////////////////////

      grossPremium: dto.premiumDevelopments?.at(0)?.grossPremium,
      netPremium: dto.premiumDevelopments?.at(0)?.netPremium,
      year: dto.premiumDevelopments?.at(0)?.year,

      conversionCapitalDevelopments:
        QuoteReducerMapperService.tryMapConversionCapitalDevelopments(
          dto.conversionCapitalDevelopments,
        ),

      /////////////////////////////////////////////////////
      // Product Configuration
      /////////////////////////////////////////////////////

      productConfiguration:
        QuoteReducerMapperService.tryMapProductConfigurationToState(
          productConfigurationDto,
        ),

      /////////////////////////////////////////////////////
      // Underwriting Questions
      /////////////////////////////////////////////////////

      underwritingQuestions:
        QuoteReducerMapperService.mapUnderwritingQuestionsToState(
          dto.underwritingQuestions,
        ),
      hasUnderwritingQuestionsPassed:
        dto.applicant?.hasUnderwritingQuestionsPassed,
      answeredUnderwritingQuestions:
        QuoteReducerMapperService.tryMapAnsweredUnderwritingQuestionsToState(
          dto.applicant?.answeredUnderwritingQuestions,
        ),

      /////////////////////////////////////////////////////
      // Data Analytics
      /////////////////////////////////////////////////////

      /**
       * We do not provide the data analytics campaign as part of the response.
       */
      dataAnalyticsCampaign: {
        utmSource: existingQuoteFeatureState.dataAnalyticsCampaign?.utmSource,
        utmMedium: existingQuoteFeatureState.dataAnalyticsCampaign?.utmMedium,
        utmCampaign:
          existingQuoteFeatureState.dataAnalyticsCampaign?.utmCampaign,
        utmContent: existingQuoteFeatureState.dataAnalyticsCampaign?.utmContent,
        utmTerm: existingQuoteFeatureState.dataAnalyticsCampaign?.utmTerm,
        utmId: existingQuoteFeatureState.dataAnalyticsCampaign?.utmId,
        gclId: existingQuoteFeatureState.dataAnalyticsCampaign?.gclId,
        referrer: existingQuoteFeatureState.dataAnalyticsCampaign?.referrer,
      },
    };

    return quoteFeatureState;
  }

  static tryMapConversionCapitalDevelopments(
    conversionCapitalDevelopmentDtos:
      | ConversionCapitalDevelopmentDto[]
      | undefined,
  ) {
    const conversionCapitalDevelopments = conversionCapitalDevelopmentDtos?.map(
      (conversionCapitalDevelopmentDto) => {
        const conversionCapitalDevelopment: ConversionCapitalDevelopment = {
          date: DateTimeService.parseIsoDate(
            conversionCapitalDevelopmentDto.date,
          ),
          amount: conversionCapitalDevelopmentDto.amount,
        };
        return conversionCapitalDevelopment;
      },
    );

    return conversionCapitalDevelopments;
  }

  static mapProductConfigurationSuccessResponseToState(
    state: QuoteFeatureState,
    productConfigurationDto: ProductConfigurationDto,
  ) {
    const productConfiguration =
      QuoteReducerMapperService.tryMapProductConfigurationToState(
        productConfigurationDto,
      );

    const mergedQuoteFeatureState: QuoteFeatureState = {
      ...state,
      isBusy: false,
      error: undefined,
      productConfiguration: productConfiguration,
    };

    return mergedQuoteFeatureState;
  }

  static tryMapProductConfigurationToState(
    productConfigurationDto: ProductConfigurationDto | undefined,
  ) {
    if (productConfigurationDto === undefined) {
      return undefined;
    }

    const parsedAllowedInsuranceStartDates =
      productConfigurationDto.allowedInsuranceStartDates.map(
        (allowedInsuranceStartDate) =>
          DateTimeService.parseIsoDate(allowedInsuranceStartDate),
      );

    const termDocuments = QuoteReducerMapperService.getTermDocuments(
      productConfigurationDto.termDocuments,
    );

    const productConfiguration: ProductConfiguration = {
      minimumInsuredSum: productConfigurationDto.minimumInsuredSum,
      maximumInsuredSum: productConfigurationDto.maximumInsuredSum,
      maximumInsuredSumWithoutMedicalCheckup:
        productConfigurationDto.maximumInsuredSumWithoutMedicalCheckup,
      minimumEntryAge: productConfigurationDto.minimumEntryAge,
      maximumEntryAge: productConfigurationDto.maximumEntryAge,
      maximumCoverageAge: productConfigurationDto.maximumCoverageAge,
      statutoryRetirementAgeFemale:
        productConfigurationDto.statutoryRetirementAgeFemale,
      statutoryRetirementAgeMale:
        productConfigurationDto.statutoryRetirementAgeMale,
      cancellationInterval: productConfigurationDto.cancellationInterval,
      bmiConfiguration: productConfigurationDto.bmiConfiguration,
      termDocuments: termDocuments,
      allowedInsuranceStartDates: parsedAllowedInsuranceStartDates,
    };

    return productConfiguration;
  }

  static getTermDocuments(termDocuments: TermDocumentDto[]) {
    return termDocuments.map((termDocumentDto) => {
      let typeOfDocument;

      switch (termDocumentDto.typeOfDocument) {
        case TermLifeInsuranceStaticDocumentType.INFO_DATA_PROCESSING:
          typeOfDocument = StaticDocumentType.INFO_DATA_PROCESSING;
          break;
        case TermLifeInsuranceStaticDocumentType.TERMS_GENERAL_INDIVIDUAL_LIFE_INSURANCE:
          typeOfDocument =
            StaticDocumentType.TERMS_GENERAL_INDIVIDUAL_LIFE_INSURANCE;
          break;
        case TermLifeInsuranceStaticDocumentType.TERMS_SUPPLEMENT_TERM_LIFE_INSURANCE:
          typeOfDocument =
            StaticDocumentType.TERMS_SUPPLEMENT_TERM_LIFE_INSURANCE;
          break;
        case TermLifeInsuranceStaticDocumentType.AGREEMENT_EMAIL_COMMUNICATION:
          typeOfDocument = StaticDocumentType.AGREEMENT_EMAIL_COMMUNICATION;
          break;
        case TermLifeInsuranceStaticDocumentType.TERMS_NON_SMOKER:
          typeOfDocument = StaticDocumentType.TERMS_NON_SMOKER;
          break;
        case TermLifeInsuranceStaticDocumentType.INFO_ELECTRONIC_DIRECT_SALES:
          typeOfDocument = StaticDocumentType.INFO_ELECTRONIC_DIRECT_SALES;
          break;
        case TermLifeInsuranceStaticDocumentType.INFO_TERM_LIFE_INSURANCE_PRODUCT:
          typeOfDocument = StaticDocumentType.INFO_TERM_LIFE_INSURANCE_PRODUCT;
          break;
        default:
          throw new UnreachableCaseError(termDocumentDto.typeOfDocument);
      }

      const termDocument: TermDocument = {
        internalDocumentName: termDocumentDto.internalDocumentName,
        typeOfDocument: typeOfDocument,
      };

      return termDocument;
    });
  }

  /**
   * This function can be used to achieve the following goals:
   * => Request a reset in the backend for a property
   * => Enrich the state before it's being submitted to the backend
   * => Set a default value for a property
   */
  static createQuoteState(
    existingQuoteFeatureState: QuoteFeatureState,
    newPartialQuoteFeatureState: QuoteFeatureState,
  ) {
    const newPartialQuoteFeatureStateWithoutUndefinedProperties: Partial<QuoteFeatureState> =
      ObjectService.removeUndefinedProperties(newPartialQuoteFeatureState);

    /**
     * It's important that we remove any property which is set to undefined before applying the spread operator.
     * A property with the key 'X' and the value 'undefined' will overwrite any existing value.
     * The spread operator will only ignore the property if the key is also 'undefined'.
     * https://devblogs.microsoft.com/typescript/announcing-typescript-4-4/#exact-optional-property-types
     */
    const mergedQuoteFeatureState: QuoteFeatureState = {
      ...existingQuoteFeatureState,
      ...newPartialQuoteFeatureStateWithoutUndefinedProperties,
    };

    const enrichedQuoteFeatureState: QuoteFeatureState =
      QuoteReducerMapperService.createEnrichedFeatureState(
        existingQuoteFeatureState,
        mergedQuoteFeatureState,
      );

    return enrichedQuoteFeatureState;
  }

  static tryMapAnsweredUnderwritingQuestionsToState(
    answeredUnderwritingQuestions:
      | AnsweredUnderwritingQuestionDto[]
      | null
      | undefined,
  ) {
    return answeredUnderwritingQuestions?.map(
      (answeredUnderwritingQuestion) => {
        const underwritingQuestionAnswer: UnderwritingQuestionAnswer = {
          questionId: answeredUnderwritingQuestion.questionId,
          saidYes: undefined,
        };

        return underwritingQuestionAnswer;
      },
    );
  }

  static tryMapBeneficiariesToState(
    personBeneficiariesDto?: PersonBeneficiaryDto[],
    organizationBeneficiariesDto?: OrganizationBeneficiaryDto[],
  ) {
    if (
      personBeneficiariesDto === undefined &&
      organizationBeneficiariesDto === undefined
    ) {
      return undefined;
    }

    const personBeneficiaries = personBeneficiariesDto?.map(
      (personBeneficiaryDto) => {
        const personBeneficiary: PersonBeneficiary = {
          type: BeneficiarySubType.PERSON,
          index: personBeneficiaryDto.index,
          percentage: personBeneficiaryDto.percentage,
          dateOfBirth: new Date(personBeneficiaryDto.dateOfBirth),
          firstName: personBeneficiaryDto.firstName,
          lastName: personBeneficiaryDto.lastName,
        };

        return personBeneficiary;
      },
    );

    const organizationBeneficiaries = organizationBeneficiariesDto?.map(
      (organizationBeneficiaryDto) => {
        const organizationBeneficiary: OrganizationBeneficiary = {
          type: BeneficiarySubType.ORGANIZATION,
          index: organizationBeneficiaryDto.index,
          percentage: organizationBeneficiaryDto.percentage,
          name: organizationBeneficiaryDto.name,
          postcode: organizationBeneficiaryDto.postcode,
          city: organizationBeneficiaryDto.city,
        };

        return organizationBeneficiary;
      },
    );

    return [
      ...(personBeneficiaries ?? []),
      ...(organizationBeneficiaries ?? []),
    ];
  }

  static mapUnderwritingQuestionsToState(
    underwritingQuestions?: UnderwritingQuestionDto[],
  ) {
    return underwritingQuestions?.map((underwritingQuestionDto) => {
      const underwritingQuestion: UnderwritingQuestion = {
        id: underwritingQuestionDto.id,
        label: underwritingQuestionDto.label,
        additionalInfo: underwritingQuestionDto.additionalInfo,
      };

      return underwritingQuestion;
    });
  }

  private static createEnrichedFeatureState(
    existingQuoteFeatureState: QuoteFeatureState,
    mergedQuoteFeatureState: QuoteFeatureState,
  ) {
    if (mergedQuoteFeatureState.productConfiguration === undefined) {
      throw new InvalidOperationException(
        `It should not be possible that the '${getKey<QuoteFeatureState>("productConfiguration")}' property in our store is 'undefined' when data is being submitted to the backend.`,
      );
    }

    const insuranceTermInYears =
      QuoteReducerMapperService.getInsuranceTermInYears(
        existingQuoteFeatureState,
        mergedQuoteFeatureState,
      );

    const monthOfPregnancy = QuoteReducerMapperService.getMonthOfPregnancy(
      existingQuoteFeatureState,
      mergedQuoteFeatureState,
    );

    const defaultInsuranceStartDate =
      DateTimeService.getFirstDayOfNextMonthDate();

    if (insuranceTermInYears !== undefined) {
      mergedQuoteFeatureState.insuranceTermInYears = insuranceTermInYears;
    }

    if (monthOfPregnancy !== undefined) {
      mergedQuoteFeatureState.monthOfPregnancy = monthOfPregnancy;
    }

    if (mergedQuoteFeatureState.insuranceStartDate === undefined) {
      mergedQuoteFeatureState.insuranceStartDate = defaultInsuranceStartDate;
    }

    return mergedQuoteFeatureState;
  }

  // Changing the 'dateOfBirth' resets the 'insuranceTermInYears' as the 'dateOfBirth' is needed to calculate the possible
  // 'insuranceTermInYears' options in the backend. This only happens if the 'insuranceTermInYears' was set to a value.
  private static getInsuranceTermInYears(
    existingQuoteFeatureState: QuoteFeatureState,
    mergedQuoteFeatureState: QuoteFeatureState,
  ) {
    const hasInsuranceTermInYears =
      !!mergedQuoteFeatureState.insuranceTermInYears;

    const hasDateOfBirthChanged = QuoteReducerMapperService.hasValueChanged(
      existingQuoteFeatureState.dateOfBirth?.toISOString(),
      mergedQuoteFeatureState.dateOfBirth?.toISOString(),
    );

    return hasDateOfBirthChanged && hasInsuranceTermInYears
      ? DEFAULT_RESET_VALUE
      : undefined;
  }

  // We always reset the 'monthOfPregnancy' whenever the gender changes
  private static getMonthOfPregnancy(
    existingQuoteFeatureState: QuoteFeatureState,
    newPartialQuoteFeatureState: QuoteFeatureState,
  ) {
    const hasGenderChanged = QuoteReducerMapperService.hasValueChanged(
      existingQuoteFeatureState.gender,
      newPartialQuoteFeatureState.gender,
    );

    return hasGenderChanged ? DEFAULT_RESET_VALUE : undefined;
  }

  private static hasValueChanged<T>(existingValue: T, newValue: T) {
    // A property can only be reset by passing in 'null' as the 'newValue'.
    // This is needed as we otherwise can not distinguish between a property which was not required on the page (e.g gender on the dateOfBirth page)
    // and a property which should actually be reset.
    // The 'newValue' comes from a partial object and only contains the data from the page which submitted it.
    const hasNewValue = newValue !== undefined;
    const hasChanged = existingValue !== newValue;

    return hasNewValue && hasChanged;
  }
}
